import _ from 'lodash';
import {connect} from 'react-redux';

import {PredicateOf} from '../../../../../libs/shared/core/src/helpers/react/reactHelpers';
import {UnreachableError} from '../../../../../libs/shared/core/src/helpers/types/customErrors';
import {InboxModel} from '../../../../../libs/shared/core/src/models/api/inboxModel';
import {collectEnumValues} from '../../../../../libs/shared/core/src/models/api/types/enumTypes';
import {
  WorkspaceDisplayChevronActionsEnum,
  WorkspaceDisplayChevronSectionsEnum,
  WorkspaceDisplaySectionTrackingTypesEnum,
} from '../../../../../libs/shared/core/src/models/tracking/contexts/workspaceTracking';
import {
  InterfaceEventsEnum,
  trackInterfaceEvent,
} from '../../../../../libs/shared/core/src/services/tracking/tracking';
import {wrapActionCreator} from '../../../../../libs/shared/core/src/store/helpers/reduxHelpers';
import {createSelector, makeMapStateToProps} from '../../../../../srcCommon/helpers/reselectHelpers';
import {CellsConversationsDragItem} from '../../../../helpers/conversationCellHelpers';
import {buildTeammateInboxLabelSectionExpandedKey} from '../../../../helpers/expansionTogglingHelpers';
import {WorkspaceFolderNestedLabelsEnum} from '../../../../models/api/workspaceModel';
import {TeammatesFoldersEnum} from '../../../../models/url/models/inboxesUrlModel';
import {Dispatch} from '../../../../store/appStore';
import {getHasInternalDiscussions} from '../../../../store/data/company/companySelectors';
import {getUserOwnedInboxes} from '../../../../store/data/inboxes/inboxesSelectors';
import {toggleExpandItemAction} from '../../../../store/data/localPreferences/localPreferencesActions';
import {
  getFindIsExpanded,
  getRefinementOptions,
} from '../../../../store/data/localPreferences/localPreferencesSelectors';
import {makeGetCountersVisibility} from '../../../../store/data/preferences/countersSelectors';
import {makeGetWorkspacesSelectedRefinements} from '../../../../store/data/refinements/refinementsSelectors';
import {
  canDropOnTeammateInboxTagAction,
  moveToTeammateInboxTagAction,
} from '../../../../store/data/teammates/teammatesActions';
import {getUser, getUserNamespace} from '../../../../store/data/teammates/teammatesSelectors';
import {reorderNestedFolderInPersonalWorkspaceAsyncAction} from '../../../../store/data/workspace/workspaceActions';
import {
  buildInboxRefinementKey,
  buildTeammateRefinementKey,
} from '../../../../store/panels/inboxes/parameters/inboxesParametersSelectors';
import {getShouldSimplifyPersonalWorkspace} from '../../../../store/stateless/cellsEmptySetupActionsSelectors';
import {canDropWorkspaceItemAtTarget} from '../dnd/workspaceDragDropHelpers';
import {WorkspaceDragSourceTypesEnum, WorkspaceItemDropHandler} from '../dnd/workspaceDragDropTypes';
import {TeammateWorkspaceInboxTag} from './teammateWorkspaceInboxTag';

/*
 * Constants.
 */

const defaultIsInboxTagSectionExpanded = true;

/*
 * Props.
 */

export interface TeammateWorkspaceInboxTagContainerProps {}

/*
 * Selectors.
 */

/** Find whether this section should be expanded or not. */
const getIsExpanded = createSelector([getUserNamespace, getFindIsExpanded], (userNamespace, findIsExpanded) =>
  findIsExpanded(buildTeammateInboxLabelSectionExpandedKey(userNamespace), defaultIsInboxTagSectionExpanded),
);

/** Find the counters visibility for the teammate's workspace. */
const getCountersVisibility = makeGetCountersVisibility(getUserNamespace);

const getUserOwnerInboxesMap = createSelector([getUserOwnedInboxes], (inboxes) =>
  _.keyBy(inboxes, (i) => i.id),
);

function makeGetShouldShowRefinedResultsIndicator() {
  return createSelector([getUser, getRefinementOptions], (user, refinementOptions) => {
    const key = buildTeammateRefinementKey(user.id, TeammatesFoldersEnum.INBOX);

    return refinementOptions[key] !== undefined;
  });
}

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<TeammateWorkspaceInboxTagContainerProps>()(() => {
  // This array is effectively a constant, we instantiate it outside the selector so that its identity remains constant.
  // This constant identiy is important from a performance standpoint, so we don't re-render P1 too frequently.
  const workspaceLabels = collectEnumValues(WorkspaceFolderNestedLabelsEnum).map((label) => ({id: label}));

  const getFoldersSelectedRefinements = makeGetWorkspacesSelectedRefinements<
    TeammateWorkspaceInboxTagContainerProps,
    {id: WorkspaceFolderNestedLabelsEnum}
  >(
    () => workspaceLabels,
    (folder, userId) => {
      switch (folder.id) {
        case WorkspaceFolderNestedLabelsEnum.SHARED_WITH_ME:
          return buildTeammateRefinementKey(userId, TeammatesFoldersEnum.SHARED_WITH_ME);
        case WorkspaceFolderNestedLabelsEnum.ASSIGNED_TO_ME:
          return buildTeammateRefinementKey(userId, TeammatesFoldersEnum.ASSIGNED);
        case WorkspaceFolderNestedLabelsEnum.DISCUSSIONS:
          return buildTeammateRefinementKey(userId, TeammatesFoldersEnum.DISCUSSIONS);
        default:
          throw new UnreachableError(folder.id);
      }
    },
  );

  const getInboxesSelectedRefinements = makeGetWorkspacesSelectedRefinements<
    TeammateWorkspaceInboxTagContainerProps,
    InboxModel
  >(getUserOwnedInboxes, (inbox) => buildInboxRefinementKey(inbox.id));
  const getShouldShowRefinedResultsIndicator = makeGetShouldShowRefinedResultsIndicator();
  return {
    user: getUser,
    inboxes: getUserOwnerInboxesMap,
    countersVisibility: getCountersVisibility,
    isExpanded: getIsExpanded,
    hasInternalDiscussions: getHasInternalDiscussions,
    shouldHideToggleExpand: getShouldSimplifyPersonalWorkspace,
    foldersSelectedRefinements: getFoldersSelectedRefinements,
    inboxesSelectedRefinements: getInboxesSelectedRefinements,
    shouldShowRefinedResultsIndicator: getShouldShowRefinedResultsIndicator,
  };
});

function mapDispatchToProps(dispatch: Dispatch) {
  return {
    toggleExpandItemAction: wrapActionCreator(dispatch, toggleExpandItemAction),
    canDropOnTeammateInboxTagAction: wrapActionCreator(dispatch, canDropOnTeammateInboxTagAction),
    moveToTeammateInboxTagAction: wrapActionCreator(dispatch, moveToTeammateInboxTagAction),
    reorderWorkspacePersonalFolder: wrapActionCreator(
      dispatch,
      reorderNestedFolderInPersonalWorkspaceAsyncAction,
    ),
  };
}

export const TeammateWorkspaceInboxTagContainer = connect(
  mapStateToProps,
  mapDispatchToProps,
  (stateProps, dispatchProps, ownProps) => {
    const {user} = stateProps;

    const onToggleExpandClick = () => {
      trackInterfaceEvent(InterfaceEventsEnum.P1_WORKSPACE_CHEVRON_CLICK, {
        location: WorkspaceDisplayChevronSectionsEnum.INBOX,
        action: stateProps.isExpanded
          ? WorkspaceDisplayChevronActionsEnum.COLLAPSE
          : WorkspaceDisplayChevronActionsEnum.EXPAND,
      });
      dispatchProps.toggleExpandItemAction(
        buildTeammateInboxLabelSectionExpandedKey(user.namespace),
        defaultIsInboxTagSectionExpanded,
      );
    };

    const canConversationsDrop: PredicateOf<CellsConversationsDragItem> = (options) =>
      dispatchProps.canDropOnTeammateInboxTagAction(options, user);

    const onConversationsDrop: (item: CellsConversationsDragItem) => void = (options) =>
      dispatchProps.moveToTeammateInboxTagAction(options, user);

    const onWorkspaceItemDrop: WorkspaceItemDropHandler = (droppedItem, linePosition, target) => {
      if (!target || !canDropWorkspaceItemAtTarget(droppedItem, target)) {
        return;
      }

      if (
        droppedItem.type !== WorkspaceDragSourceTypesEnum.PERSONAL_FOLDER ||
        target.type !== WorkspaceDragSourceTypesEnum.PERSONAL_FOLDER
      ) {
        return;
      }

      trackInterfaceEvent(InterfaceEventsEnum.P1_WORKSPACE_REORDER, {
        section: WorkspaceDisplaySectionTrackingTypesEnum.INDIV_SUB_INBOX,
      });
      dispatchProps.reorderWorkspacePersonalFolder({
        droppedFolder: droppedItem,
        targetFolder: target,
        linePosition,
      });
    };

    return {
      ...stateProps,
      ...ownProps,
      onToggleExpandClick: stateProps.shouldHideToggleExpand ? undefined : onToggleExpandClick,
      canConversationsDrop,
      onConversationsDrop,
      onWorkspaceItemDrop,
    };
  },
)(TeammateWorkspaceInboxTag);
